// src/layouts/DashboardLayout.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import TourGuide from '../components/TourGuide';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { trackEvent } from '../utils/track';

import { ReactComponent as HomeIcon } from '../assets/icon-home.svg';
import { ReactComponent as HomeIconYellow } from '../assets/icon-home-y.svg';
import { ReactComponent as DoorloopjesIcon } from '../assets/icon-book-open.svg';
import { ReactComponent as DoorloopjesIconYellow } from '../assets/icon-book-open-y.svg';
import { ReactComponent as DigidatesIcon } from '../assets/icon-headphones.svg';
import { ReactComponent as DigidatesIconYellow } from '../assets/icon-headphones-y.svg';
import { ReactComponent as KennisbankIcon } from '../assets/icon-feather.svg';
import { ReactComponent as KennisbankIconYellow } from '../assets/icon-feather-y.svg';
import { ReactComponent as ClusterIcon } from '../assets/icon-octagon.svg';
import { ReactComponent as ClusterIconYellow } from '../assets/icon-octagon-y.svg';
import { ReactComponent as NewsIcon } from '../assets/icon-news.svg';
import { ReactComponent as NewsIconYellow } from '../assets/icon-news-y.svg';
import { ReactComponent as VideoAnalyseIcon } from '../assets/icon-video-analyse.svg';
import { ReactComponent as VideoAnalyseIconYellow } from '../assets/icon-video-analyse-y.svg';

import { ReactComponent as PhoneIcon } from '../assets/icon-phone.svg';
import { ReactComponent as GesprekkenIcon } from '../assets/icon-chats.svg';
import { ReactComponent as GesprekkenIconYellow } from '../assets/icon-chats-y.svg';
import InfoModal from '../components/InfoModal';
import SchoolPromptModal from '../components/SchoolPromptModal';
import AankondigingModal from '../components/AankondigingModal';
import CookieSettingsLink from '../components/CookieSettingsLink';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

const TOUR_DISMISSED_KEY = 'doorloopjes_tour_dismissed';

// Zoektermen loggen: pas als er 1,5 s niet is getypt, vanaf 3 tekens.
const ZOEK_LOG_VERTRAGING_MS = 1500;
const ZOEK_LOG_MIN_TEKENS = 3;

const normaliseerZoekterm = (term) => (term || '').trim().toLowerCase().slice(0, 80);

const ZOEK_ROUTES = {
  doorloopje: (slug) => `/doorloopjes/${slug}`,
  artikel: (slug) => `/artikelen/${slug}`,
  why: (slug) => `/why/${slug}`,
  how: (slug) => `/how/${slug}`,
};

const getTourRoutes = (hasVideoAnalysis) => {
  const routes = [
    '/dashboard',      // 0. Intro
    '/dashboard',      // 1. Home
    '/doorloopjes',    // 2. Doorloopjes
    '/nieuws',         // 3. Nieuws
  ];
  if (hasVideoAnalysis) {
    routes.push('/video-analyse'); // 4. Video Analyse
  }
  routes.push(
    '/cluster-builder-test', // Clusters
    '/achtergronden',        // Kennisbank
    '/webinars',             // Webinars
    '/webinars',             // Contact (zelfde pagina)
    '/dashboard',            // Profiel
  );
  return routes;
};

const getInitials = (name) => {
  if (!name) return '';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0].substring(0, 2).toUpperCase();
};

const SidebarButton = ({ to, exact = true, Icon, ActiveIcon, children, onClick }) => (
  <NavLink to={to} className="sidebar-btn" end={exact} onClick={onClick}>
    <Icon className="sidebar-btn-icon" />
    <ActiveIcon className="sidebar-btn-icon-active" />
    <span className="sidebar-btn-text">{children}</span>
  </NavLink>
);

const SidebarActionButton = ({ onClick, title, Icon, children, extraClass = '' }) => (
  <button
    type="button"
    className={`sidebar-btn ${extraClass}`}
    onClick={onClick}
    title={title}
    aria-label={title}
  >
    {Icon ? <Icon /> : null}
    <span className="sidebar-btn-text">{children}</span>
  </button>
);

const SidebarIconButton = ({ onClick, title, children, imgSrc }) => (
  <button
    type="button"
    className="sidebar-btn sidebar-ai-launcher"
    onClick={onClick}
    title={title}
    aria-label={title}
  >
    <img
      src={imgSrc}
      alt=""
      className="sidebar-logo-img"
      style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
    />
    <span className="sidebar-btn-text">{children}</span>
  </button>
);

const DashboardLayout = () => {
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [showAnimatedSearchDropdown, setShowAnimatedSearchDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [zoekFout, setZoekFout] = useState(false);
  // Laatst afgeronde zoekopdracht (term + aantal getoonde resultaten), alleen om
  // te loggen. Mislukte zoekopdrachten komen hier niet in en worden dus ook niet
  // gelogd: nul resultaten door een fout zegt niets over de inhoud.
  const [laatsteZoekactie, setLaatsteZoekactie] = useState(null);

  const [contactLoading, setContactLoading] = useState(false);
  const [contactData, setContactData] = useState(null);

  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );

  const {
    currentUser,
    markTourAsCompleted,
    tourState,
    setTourState,
    startTour,
    stopTour,
    globalSettings,
    loading: globalSettingsLoading,
  } = useDoorloopjes();

  const navigate = useNavigate();
  const location = useLocation();
  const searchDropdownWrapperRef = useRef(null);
  const contentScrollWrapperRef = useRef(null);
  const laatsteToetsRef = useRef(0);
  const gelogdeZoektermRef = useRef(null);

  // Auto-start guard
  const tourCheckPerformed = useRef(false);

  // Sessie-dismiss guard
  const tourDismissedRef = useRef(false);

  useEffect(() => {
    try {
      tourDismissedRef.current = sessionStorage.getItem(TOUR_DISMISSED_KEY) === '1';
    } catch {}
  }, []);

  // Zonder eigen dashboardlogo in de instellingen: het Doorloopjes-beeld.
  const dashboardLogoUrl = globalSettings?.dashboardLogo?.url || '/favicon.png';

  useEffect(() => {
    trackEvent({ type: 'pageview', path: location.pathname + location.search, title: document.title });
  }, [location.pathname, location.search]);

  // Auto-start: markeer direct als uitgevoerd vóór we iets starten (voorkomt race met Help)
  useEffect(() => {
    if (!currentUser || tourCheckPerformed.current) return;

    tourCheckPerformed.current = true; // <— meteen true zetten

    // Lees uit de ref in plaats van sessionStorage om de raceconditie te voorkomen.
    const dismissed = tourDismissedRef.current;

    if (!currentUser.has_completed_tour && !dismissed) {
      startTour();
    }
  }, [currentUser, startTour]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mm = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    if (mm.addEventListener) mm.addEventListener('change', handler);
    else mm.addListener(handler);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener('change', handler);
      else mm.removeListener(handler);
    };
  }, []);

  // Joyride callback — sluiting/overslaan cancelt pending timers en stopt tour
  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;

    if (status === STATUS.FINISHED) {
      stopTour();
      setIsMoreOpen(false);
      markTourAsCompleted();
      return;
    }

    if (status === STATUS.SKIPPED) {
      stopTour();
      setIsMoreOpen(false);

      if (action === ACTIONS.SKIP) {
        // ‘Overslaan’ telt als afgerond
        markTourAsCompleted();
      } else if (action === ACTIONS.CLOSE) {
        // ‘X’ is alleen sluiten: markeer als dismissed voor deze sessie
        try { sessionStorage.setItem(TOUR_DISMISSED_KEY, '1'); } catch {}
        tourDismissedRef.current = true;
      }
      return;
    }

    // Alleen bewegen bij NEXT/PREV
    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      if (action !== ACTIONS.NEXT && action !== ACTIONS.PREV) return;

      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      const tourRoutes = getTourRoutes(currentUser?.has_video_analysis);
      const nextRoute = tourRoutes[nextStepIndex];
      if (nextRoute && nextRoute !== location.pathname) navigate(nextRoute);
      setTourState({ run: true, stepIndex: nextStepIndex });
    }
  };

  // Help-klik: reset dismissed, annuleer pending timers, start schoon
  const handleHelpClick = () => {
    try { sessionStorage.removeItem(TOUR_DISMISSED_KEY); } catch {}
    tourDismissedRef.current = false;

    // voorkom dat auto-start later nog inhaalt (al gemarkeerd, maar zeker weten)
    tourCheckPerformed.current = true;

    // stop eventueel lopende/pending tour → start opnieuw
    stopTour();
    startTour();
  };

  useEffect(() => {
    if (!isMobile || !tourState?.run) {
      setIsMoreOpen(false);
      return;
    }
    // Clusters, Kennisbank, Webinars en Contact zitten op mobiel in "Meer"
    const offset = currentUser?.has_video_analysis ? 5 : 4;
    const NEED_MORE = new Set([offset, offset + 1, offset + 2, offset + 3]);
    setIsMoreOpen(NEED_MORE.has(tourState.stepIndex));
  }, [isMobile, tourState?.run, tourState?.stepIndex]);

  useEffect(() => {
    if (contentScrollWrapperRef.current) contentScrollWrapperRef.current.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (searchDropdownWrapperRef.current && !searchDropdownWrapperRef.current.contains(e.target)) {
        setIsSearchDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    let timer;
    if (isSearchDropdownOpen) {
      timer = setTimeout(() => setShowAnimatedSearchDropdown(true), 50);
    } else {
      setShowAnimatedSearchDropdown(false);
    }
    return () => clearTimeout(timer);
  }, [isSearchDropdownOpen]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setIsSearchDropdownOpen(false);
      return;
    }
    setIsSearchLoading(true);
    // Een antwoord op een eerdere zoekterm mag een nieuwer antwoord niet overschrijven.
    let actueel = true;
    const delay = setTimeout(() => {
      const jwt = localStorage.getItem('jwt');
      const headers = { Authorization: `Bearer ${jwt}` };

      // De backend zoekt in titels, stappen, thema en tekst van doorloopjes en
      // achtergrondinformatie (GET /api/zoeken); hier alleen nog label en route erbij.
      axios
        .get(`${STRAPI_URL}/api/zoeken`, { headers, params: { q: searchTerm } })
        .then((res) => {
          if (!actueel) return;
          const resultaten = (res.data?.resultaten || []).map((r) => ({
            id: r.slug,
            title: r.titel,
            __type: r.type,
            __typeLabel: r.type === 'doorloopje' ? 'Doorloopje' : 'Achtergrondinformatie',
            __to: ZOEK_ROUTES[r.type](r.slug),
            __img: r.afbeelding,
          }));
          setSearchResults(resultaten);
          setZoekFout(false);
          // Ook zonder resultaten open: dan staat er "Geen resultaten gevonden" in plaats van niets.
          setIsSearchDropdownOpen(true);
          setLaatsteZoekactie({ term: searchTerm, results: resultaten.length });
        })
        .catch((err) => {
          // Geen "Geen resultaten": de backend (of het netwerk) faalde. Deze term ook niet
          // loggen, anders telt hij in de analytics als zoekterm zonder resultaat.
          console.error('Fout bij zoeken:', err);
          if (!actueel) return;
          setSearchResults([]);
          setZoekFout(true);
          setIsSearchDropdownOpen(true);
        })
        .finally(() => {
          if (actueel) setIsSearchLoading(false);
        });
    }, 300);
    return () => {
      actueel = false;
      clearTimeout(delay);
    };
  }, [searchTerm]);

  // Zoektermen loggen voor de redactie: welke termen worden gezocht en hoeveel
  // resultaten leveren ze op. Staat los van het zoeken zelf, zodat een trage of
  // mislukte POST de zoekbalk niet raakt. Pas als de resultaten van precies deze
  // term binnen zijn, en nooit twee keer dezelfde term achter elkaar.
  const logZoekterm = (term, resultaten) => {
    if (term.length < ZOEK_LOG_MIN_TEKENS || gelogdeZoektermRef.current === term) return;
    gelogdeZoektermRef.current = term;
    trackEvent({ type: 'search', path: location.pathname, payload: { term, results: resultaten } });
  };

  useEffect(() => {
    const term = normaliseerZoekterm(searchTerm);
    if (term.length < ZOEK_LOG_MIN_TEKENS) return;
    if (!laatsteZoekactie || normaliseerZoekterm(laatsteZoekactie.term) !== term) return;
    if (gelogdeZoektermRef.current === term) return;

    const resultaten = laatsteZoekactie.results;
    const wacht = Math.max(0, ZOEK_LOG_VERTRAGING_MS - (Date.now() - laatsteToetsRef.current));
    const timer = setTimeout(() => logZoekterm(term, resultaten), wacht);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, laatsteZoekactie, location.pathname]);

  // Wie binnen de wachttijd op een resultaat klikt, wist de zoekterm en daarmee de timer
  // hierboven. Dan nu loggen, anders ontbreken juist de snelle, geslaagde zoekopdrachten.
  const kiesZoekresultaat = () => {
    if (laatsteZoekactie) logZoekterm(normaliseerZoekterm(laatsteZoekactie.term), laatsteZoekactie.results);
    setIsSearchDropdownOpen(false);
    setSearchTerm('');
  };

  const [isContactOpen, setIsContactOpen] = useState(false); // shadow var fix
  const [schoolPromptOpen, setSchoolPromptOpen] = useState(false);
  const openContactOverlay = async () => {
    setIsContactOpen(true);
    if (contactData) return;
    setContactLoading(true);
    try {
      const res = await axios.get(`${STRAPI_URL}/api/contact-pagina?populate=*`);
      setContactData(res.data.data);
    } catch {
      try {
        const jwt = localStorage.getItem('jwt');
        const res2 = await axios.get(`${STRAPI_URL}/api/contact-pagina?populate=*`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        setContactData(res2.data.data);
      } catch (e) {
        console.error('Contact-pagina ophalen faalde:', e);
      }
    } finally {
      setContactLoading(false);
    }
  };

  if (globalSettingsLoading || !currentUser) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <p>Layout laden...</p>
      </div>
    );
  }

  // Open Assistant Logic
  const openAssistant = () => {
    // Explicitly check current mobile state to avoid stale closure issues
    const currentIsMobile = window.matchMedia('(max-width: 768px)').matches;

    if (window.PAP_Chat && window.PAP_Chat.open) {
      // On mobile, force maximized state
      const targetState = currentIsMobile ? 'maximized' : 'normal';
      window.PAP_Chat.open({ state: targetState });
    } else {
      window.dispatchEvent(new CustomEvent('open-ai-assistant'));
    }
  };

  const MoreOverlay = () =>
    isMoreOpen ? (
      <>
        <div
          onClick={() => setIsMoreOpen(false)}
          aria-hidden="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(35,31,32,0.45)', zIndex: 1100 }}
        />
        <div
          role="dialog"
          aria-label="Meer menu"
          className="sidebar-more-sheet"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            background: '#fff',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            boxShadow: '0 -8px 24px rgba(0,0,0,0.2)',
            zIndex: 1101,
            padding: '16px',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {currentUser?.has_video_analysis && (
              <SidebarButton to="/video-analyse" exact Icon={VideoAnalyseIcon} ActiveIcon={VideoAnalyseIconYellow} onClick={() => setIsMoreOpen(false)}>
                Video Analyse
              </SidebarButton>
            )}
            <SidebarButton to="/gesprekken" exact Icon={GesprekkenIcon} ActiveIcon={GesprekkenIconYellow} onClick={() => setIsMoreOpen(false)}>
              Gesprekken
            </SidebarButton>
            <SidebarButton to="/achtergronden" exact Icon={KennisbankIcon} ActiveIcon={KennisbankIconYellow} onClick={() => setIsMoreOpen(false)}>
              Kennisbank
            </SidebarButton>
            <SidebarButton to="/webinars" exact Icon={DigidatesIcon} ActiveIcon={DigidatesIconYellow} onClick={() => setIsMoreOpen(false)}>
              Webinars
            </SidebarButton>
            <SidebarActionButton onClick={() => { setIsMoreOpen(false); openContactOverlay(); }} title="Contact" Icon={PhoneIcon} extraClass="sidebar-contact-btn">
              Contact
            </SidebarActionButton>
          </div>
        </div>
      </>
    ) : null;

  return (
    <div className="dashboard-layout">
      <TourGuide run={tourState.run} stepIndex={tourState.stepIndex} callback={handleJoyrideCallback} hasVideoAnalysis={!!currentUser?.has_video_analysis} />

      <aside className="dashboard-sidebar">
        <Link to="/dashboard" className="sidebar-logo-container">
          <img src={dashboardLogoUrl} alt="Dashboard Logo" className="sidebar-logo-img" />
        </Link>
        <nav className="sidebar-main-nav">
          {isMobile && (location.pathname !== '/dashboard' || isMobile) && (
            <SidebarIconButton onClick={openAssistant} title="Assistent" imgSrc={dashboardLogoUrl}>Assistent</SidebarIconButton>
          )}
          <SidebarButton to="/dashboard" exact Icon={HomeIcon} ActiveIcon={HomeIconYellow}>Home</SidebarButton>
          <SidebarButton to="/doorloopjes" exact Icon={DoorloopjesIcon} ActiveIcon={DoorloopjesIconYellow}>Doorloopjes</SidebarButton>
          <SidebarButton to="/nieuws" exact Icon={NewsIcon} ActiveIcon={NewsIconYellow}>Nieuws</SidebarButton>

          {/* Video Analyse: desktop in hoofdnav, mobiel in "Meer" */}
          {!isMobile && currentUser?.has_video_analysis && (
            <SidebarButton to="/video-analyse" exact Icon={VideoAnalyseIcon} ActiveIcon={VideoAnalyseIconYellow}>
              Video Analyse
            </SidebarButton>
          )}

          {/* Clusters, Kennisbank en Webinars verbergen op mobiel */}
          {!isMobile && (
            <>
              <SidebarButton to="/cluster-builder-test" exact Icon={ClusterIcon} ActiveIcon={ClusterIconYellow}>
                Clusters
              </SidebarButton>
              <SidebarButton to="/achtergronden" exact Icon={KennisbankIcon} ActiveIcon={KennisbankIconYellow}>Kennisbank</SidebarButton>
              <SidebarButton to="/webinars" exact Icon={DigidatesIcon} ActiveIcon={DigidatesIconYellow}>Webinars</SidebarButton>
            </>
          )}
          {isMobile && <SidebarActionButton onClick={() => setIsMoreOpen(true)} title="Meer">Meer</SidebarActionButton>}
        </nav>
        <div className="sidebar-bottom">
          {!isMobile && (
            <>
              <SidebarActionButton onClick={openContactOverlay} title="Contact" Icon={PhoneIcon} extraClass="sidebar-contact-btn">
                Contact
              </SidebarActionButton>
              {location.pathname !== '/dashboard' && (
                <SidebarIconButton onClick={openAssistant} title="Assistent" imgSrc={dashboardLogoUrl}>Assistent</SidebarIconButton>
              )}
            </>
          )}
        </div>
      </aside>

      <div className="dashboard-main-content">
        <header className="dashboard-topbar">
          <div className="topbar-left-actions">
            <button className="help-button" onClick={handleHelpClick}>Uitleg/Help</button>
          </div>
          <div className="search-bar-wrapper" ref={searchDropdownWrapperRef}>
            <input
              type="search"
              placeholder="Zoeken in doorloopjes en kennisbank"
              value={searchTerm}
              onChange={(e) => {
                laatsteToetsRef.current = Date.now();
                setSearchTerm(e.target.value);
              }}
              onFocus={() => setIsSearchDropdownOpen(true)}
            />
            {isSearchDropdownOpen && (
              <div className={`search-results-dropdown ${showAnimatedSearchDropdown ? 'show' : ''}`}>
                {isSearchLoading ? (
                  <div className="search-status">Zoeken...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((result) => {
                    return (
                      <Link
                        key={`${result.__type}-${result.id}`}
                        to={result.__to}
                        className="search-result-item"
                        onClick={kiesZoekresultaat}
                      >
                        {result.__img ? (
                          <img src={result.__img} alt={result.title} className="search-result-icon" />
                        ) : (
                          <div className="search-result-icon placeholder"></div>
                        )}
                        <div className="search-result-content">
                          <span className="search-result-text">{result.title}</span>
                          <span className={`search-result-badge search-result-badge--${result.__type}`}>
                            {result.__typeLabel}
                          </span>
                        </div>
                      </Link>
                    );
                  })
                ) : zoekFout ? (
                  <div className="search-status">Zoeken lukt nu even niet. Probeer het zo nog eens.</div>
                ) : (
                  <div className="search-status">Geen resultaten gevonden.</div>
                )}
              </div>
            )}
          </div>
          <Link to="/profile" className="user-profile-menu">
            <div className="user-profile-trigger">
              <span className="user-name">{currentUser.fullName || currentUser.username}</span>
              <div className="user-avatar">{getInitials(currentUser.fullName || currentUser.username)}</div>
            </div>
          </Link>
        </header>
        <div className="content-scroll-wrapper" ref={contentScrollWrapperRef}>
          <Outlet context={{ startTour: handleHelpClick }} />
          <footer className="dashboard-footer">
            <CookieSettingsLink />
          </footer>
        </div>
      </div>

      <InfoModal show={isContactOpen} onClose={() => setIsContactOpen(false)} title={contactData?.title} content={contactData?.body} loading={contactLoading} showSocial />
      <MoreOverlay />
      <SchoolPromptModal onOpenChange={setSchoolPromptOpen} />
      <AankondigingModal andereModalOpen={isContactOpen || schoolPromptOpen} />
    </div>
  );
};

export default DashboardLayout;